///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { GtthModifyLinkInfoCommand } from './modify-link-info-command';
import { LinkFeature } from '../../../entities/link/link-feature';
import { GtthPreloaderModalEvent } from '../../../../../shared/preloader-modal/preloader-modal-event';
import { GtthLinkFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { _cloneDeep } from '../../../../../shared/utils';

export class GtthFreezeSelectedLinksCommand extends GtthAbstractCommand {
    private linksList: GtthLinkFeatureDtoModel[];
    private newIsUserForced: boolean;
    private newIsInstaled: boolean;

    public static applyLinkFreezeStateToBomDevices(changedLink: LinkFeature): void {
        const linkProducts = changedLink.historyService.bomReportService.getLinkProducts(changedLink);

        linkProducts.forEach((linkProduct) => {
            linkProduct.device.userForced = linkProduct.device
                .getNeighboursNames()
                .some((neighborName: string) =>
                    changedLink.historyService.logicalTopology.getLinkByNodeNames(linkProduct.node.getName(), neighborName).isUserForced()
                );
            linkProduct.device.installed = linkProduct.device
                .getNeighboursNames()
                .some((neighborName: string) =>
                    changedLink.historyService.logicalTopology.getLinkByNodeNames(linkProduct.node.getName(), neighborName).isInstalled()
                );
        });
    }

    getCommandName(): string {
        return 'GtthFreezeSelectedLinksCommand';
    }

    createFromLinksList(linksList: LinkFeature[], newIsUserForced: boolean, newIsInstalled: boolean) {
        this.linksList = linksList.map((link) => {
            return link.toGeoJson();
        });
        this.newIsUserForced = newIsUserForced;
        this.newIsInstaled = newIsInstalled;

        return this;
    }

    doCommand(): void {
        const links = this.linksList
            .map((link: GtthLinkFeatureDtoModel) => {
                const newLink: GtthLinkFeatureDtoModel = _cloneDeep(link);
                newLink.setIsUserForced(this.newIsUserForced);
                newLink.setIsInstalled(this.newIsInstaled);
                return newLink;
            })
            .sort(
                // sort links by actual sector id - in this way frozen ids will be actual id + 1
                (linkA, linkB) => linkA.getP2mpRepSectorIdx() - linkB.getP2mpRepSectorIdx()
            );

        const activity = this.createActivityDTO(false);
        activity.links = [...links];
        this.sendActivity(activity);
    }

    undoCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        const activity = this.createActivityDTO(false);
        activity.links = [...this.linksList];
        this.sendActivity(activity);
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        this.activityHandler(activityDto);
    }

    undoCommandActivity(activityDto: ActivityDTO): void {
        this.activityHandler(activityDto);
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    private activityHandler(activityDto: ActivityDTO) {
        if (!activityDto.successful) {
            if (this.linksList) {
                this.historyService.notificationService.showError(activityDto?.error?.message);

                console.log('Command failed:', activityDto.error);
            }

            this.commandEnd(activityDto.successful);
        } else {
            const commandsArr = [];

            activityDto.links.forEach((linkJson: GtthLinkFeatureDtoModel) => {
                const link = this.historyService.logicalTopology.getLink(linkJson.getId());

                const modifyCommand = this.historyService.command(GtthModifyLinkInfoCommand).create(link.getId());
                modifyCommand.setOldIsUserForced(link.isUserForced());
                modifyCommand.setNewIsUserForced(linkJson.getIsUserForced());
                modifyCommand.setOldIsInstalled(link.isInstalled());
                modifyCommand.setNewIsInstalled(linkJson.getIsInstalled());
                modifyCommand.setOldSectorInfo(link.getSectorIndex(), link.getSectorApIndex(), link.getSectorDirectionAngle());
                modifyCommand.setNewSectorInfo(linkJson.getSectorIdx(), linkJson.getSectorApIdx(), linkJson.getSectorDirection());
                commandsArr.push(this.historyService.doCommandBypassHistory(modifyCommand, true));

                GtthFreezeSelectedLinksCommand.applyLinkFreezeStateToBomDevices(link);
            });

            Promise.all(commandsArr).then((_result) => {
                this.historyService.bomReportService.saveBom();
                this.commandEnd(activityDto.successful);
            });
        }
    }

    private commandEnd(isSuccessful: boolean) {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.hide()
        });

        if (this.commandResolve) {
            this.commandResolve(isSuccessful);
        }
    }
}
